export interface CustomerProfile {
  id: string;
  fullName: string;
  email: string;
  assignedSpecialist: string | null;
  onboardingStatus: string;
}

export interface Contact {
  id: string;
  fullName: string;
  email: string;
  phoneNumber?: string;
  role: string;
  status: string;
  verifiedAt: string | null;
}

export interface Asset {
  id: string;
  customerId: string;
  assetType: string;
  networkOrCoin: string;
  provider: string | null;
  label: string;
  notes: string | null;
  lastAccessTestAt: string | null;
  status: string;
}

export interface Policy {
  id: string;
  customerId: string;
  policyName: string;
  policyType: string;
  description: string | null;
  requiredSignatures: number;
  totalSignatures: number;
  publicKeys: string[];
  bitzoIsCoordinator: boolean;
  recoveryContactIds: string[] | null;
  assets?: Asset[];
  recoveryContacts?: Contact[];
  assetsCount?: number;
  recoveryContactsCount?: number;
  verifiedRecoveryContactsCount?: number;
}

export interface RecoveryCase {
  id: string;
  customerId: string;
  policyId: string;
  caseType: string;
  status: string;
  assignedSpecialist: string | null;
  description: string;
  coordinationNotes: string | null;
  initiatedAt: string;
  approvedAt: string | null;
  completedAt: string | null;
}

export interface Readiness {
  verifiedContactsCount: number;
  requiredContactsPresent: boolean;
  policiesCount: number;
  policiesWithRecoveryContactsCount: number;
  assetsCount: number;
  assetsWithLastAccessTestCount: number;
  hasUpcomingReview: boolean;
  anyOpenRecoveryCase: boolean;
  score: number;
}

export interface AuditLog {
  id: string;
  customerId: string;
  entityType: string;
  entityId: string;
  action: string;
  performedBy: string;
  timestamp: string;
}

export interface DashboardData {
  customer: {
    id: string;
    fullName: string;
    email: string;
    assignedSpecialist: string | null;
    onboardingStatus: string;
    lastReviewAt: string | null;
    nextReviewAt: string | null;
  };
  policies: Policy[];
  contacts: Contact[];
  assets: Asset[];
  recoveryCases: RecoveryCase[];
  readiness: Readiness;
  nextActions: string[];
  recentAudit: AuditLog[];
}

async function fetchApi<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Request failed" }));
    throw new Error(error.error || "Request failed");
  }
  
  return response.json();
}

export const customerApi = {
  login: (email: string, password: string) =>
    fetchApi<CustomerProfile>("/api/auth/customer/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  logout: () =>
    fetchApi<{ success: boolean }>("/api/auth/customer/logout", {
      method: "POST",
    }),

  me: () =>
    fetchApi<{ authenticated: boolean } & Partial<CustomerProfile>>("/api/auth/customer/me"),

  dashboard: () => fetchApi<DashboardData>("/api/me/dashboard"),

  policies: () => fetchApi<Policy[]>("/api/me/policies"),

  policy: (id: string) => fetchApi<Policy>(`/api/me/policies/${id}`),

  assets: () => fetchApi<Asset[]>("/api/me/assets"),

  contacts: () => fetchApi<Contact[]>("/api/me/contacts"),

  recoveryCases: () => fetchApi<RecoveryCase[]>("/api/me/recovery-cases"),

  createRecoveryCase: (data: { policyId: string; caseType: string; description: string }) =>
    fetchApi<RecoveryCase>("/api/me/recovery-cases", {
      method: "POST",
      body: JSON.stringify(data),
    }),
};
